import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Flex,
    Text,
    Button,
    Input,
    Tabs,
    TabList,
    Tab,
    VStack,
    HStack,
    Badge,
    Avatar,
    Grid,
    GridItem,
    Card,
    CardBody,
    Divider,
    IconButton,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Textarea,
    Select,
    useDisclosure,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaSearch, FaFire, FaComments, FaUsers, FaStar, FaArrowUp, FaTrophy, FaThumbsUp, FaThumbsDown, FaPaperPlane } from "react-icons/fa";
import { fetchDiscussions, fetchPromotions, fetchCommunityStats, createNewDiscussion, fetchLeaderboard, voteOnDiscussion, fetchDiscussionComments, addCommentToDiscussion } from "./api/mockForumApi";

const MotionBox = motion(Box);

const Forum = () => {
    const [discussions, setDiscussions] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]); // New state for leaderboard
    const [communityStats, setCommunityStats] = useState({
        activeUsers: 0,
        totalPosts: 0,
        expertMembers: 0,
    });
    const [selectedCategory, setSelectedCategory] = useState("All"); // State for selected category
    const { isOpen, onOpen, onClose } = useDisclosure(); // For new topic modal
    const [newTopic, setNewTopic] = useState({
        title: "",
        content: "",
        category: "General",
    });
    const [openDiscussionId, setOpenDiscussionId] = useState(null); // State for currently open accordion discussion
    const [discussionComments, setDiscussionComments] = useState([]); // State for discussion comments
    const [newCommentContent, setNewCommentContent] = useState(""); // State for new comment input

    useEffect(() => {
        const getForumData = async () => {
            const [discussionsData, promotionsData, statsData, leaderboardData] = await Promise.all([
                fetchDiscussions(selectedCategory), // Pass selectedCategory
                fetchPromotions(),
                fetchCommunityStats(),
                fetchLeaderboard(), // Fetch leaderboard data
            ]);
            setDiscussions(discussionsData);
            setPromotions(promotionsData);
            setCommunityStats(statsData);
            setLeaderboard(leaderboardData); // Set leaderboard data
        };

        getForumData();

        const interval = setInterval(async () => {
            const statsData = await fetchCommunityStats();
            setCommunityStats(statsData);
        }, 5000); // Update stats every 5 seconds

        return () => clearInterval(interval);
    }, [selectedCategory]); // Re-run effect when selectedCategory changes

    const handleTabChange = (index) => {
        const categories = ["All", "Casino", "Sports", "Blog"]; // Map index to category
        setSelectedCategory(categories[index]);
    };

    const handleNewTopicChange = (e) => {
        const { name, value } = e.target;
        setNewTopic((prev) => ({ ...prev, [name]: value }));
    };

    const handleCreateTopic = async () => {
        if (!newTopic.title || !newTopic.content) {
            alert("Please fill in all required fields.");
            return;
        }
        const createdDiscussion = await createNewDiscussion({
            title: newTopic.title,
            content: newTopic.content, // Although not displayed, it's good to pass it
            author: "CurrentUser", // Placeholder for now
            avatar: "https://bit.ly/broken-link", // Placeholder for now
            category: newTopic.category,
        });
        setDiscussions((prev) => [createdDiscussion, ...prev]);
        setNewTopic({ title: "", content: "", category: "General" }); // Reset form
        onClose();
    };

    const handleVote = async (discussionId, voteType, event) => {
        event.stopPropagation(); // Prevent opening the discussion modal
        const result = await voteOnDiscussion(discussionId, voteType);
        if (result.success) {
            setDiscussions((prevDiscussions) =>
                prevDiscussions.map((d) =>
                    d.id === discussionId ? { ...d, upvotes: result.newUpvotes } : d
                )
            );
        } else {
            alert(result.message);
        }
    };

    return (
        <Box
            minH="100vh"
            bg="gray.900"
            color="white"
            backgroundImage="radial-gradient(circle at 25% 25%, rgba(255,0,150,0.10) 0%, transparent 60%), radial-gradient(circle at 75% 75%, rgba(0,150,255,0.10) 0%, transparent 60%)"
        >
            <Box bg="gray.800" borderBottom="1px" borderColor="gray.700">
                <Container maxW="1200px" py={4}>
                    <Flex
                        direction={{ base: "column", md: "row" }}
                        justify="space-between"
                        align={{ base: "flex-start", md: "center" }}
                        gap={4}
                    >
                        <Text fontSize="2xl" fontWeight="bold" color="purple.400">
                            WAGER STREET FORUM
                        </Text>
                        <HStack spacing={4} w={{ base: "full", md: "auto" }}>
                            <Input
                                placeholder="Search discussions..."
                                size="sm"
                                maxW={{ base: "full", md: "300px" }}
                                bg="gray.700"
                                border="none"
                                _placeholder={{ color: "gray.400" }}
                            />
                            <IconButton
                                icon={<FaSearch />}
                                size="sm"
                                colorScheme="purple"
                                aria-label="Search"
                            />
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Box bg="gray.800" borderBottom="1px" borderColor="gray.700">
                <Container maxW="1200px">
                    <Tabs variant="enclosed" colorScheme="purple" onChange={handleTabChange}>
                        <TabList border="none">
                            <Tab _selected={{ bg: "purple.600", color: "white" }}>
                                <HStack>
                                    <FaComments />
                                    <Text>All</Text>
                                </HStack>
                            </Tab>
                            <Tab _selected={{ bg: "purple.600", color: "white" }}>
                                <HStack>
                                    <Text>🎰</Text>
                                    <Text>Casino</Text>
                                </HStack>
                            </Tab>
                            <Tab _selected={{ bg: "purple.600", color: "white" }}>
                                <HStack>
                                    <Text>⚽</Text>
                                    <Text>Sports</Text>
                                </HStack>
                            </Tab>
                        </TabList>
                    </Tabs>
                </Container>
            </Box>

            <Container maxW="1200px" py={8}>
                <Grid templateColumns={{ base: "1fr", lg: "1fr 300px" }} gap={8}>
                    <GridItem>
                        <MotionBox
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            mb={6}
                        >
                            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                                {promotions.map((p, i) => (
                                    <Card
                                        key={i}
                                        bg="gray.800"
                                        border="1px"
                                        borderColor="gray.700"
                                        _hover={{ borderColor: `${p.color}.500` }}
                                    >
                                        <CardBody>
                                            <VStack align="start" spacing={2}>
                                                <Text
                                                    fontSize="lg"
                                                    fontWeight="bold"
                                                    color={`${p.color}.400`}
                                                >
                                                    {p.title}
                                                </Text>
                                                <Text color="gray.300">{p.description}</Text>
                                                <Button size="sm" colorScheme={p.color}>
                                                    Claim Now
                                                </Button>
                                            </VStack>
                                        </CardBody>
                                    </Card>
                                ))}
                            </Grid>
                        </MotionBox>

                        <MotionBox
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.15 }}
                        >
                            <Flex
                                direction={{ base: "column", md: "row" }}
                                justify="space-between"
                                align={{ base: "flex-start", md: "center" }}
                                mb={6}
                                gap={4}
                            >
                                <HStack>
                                    <FaFire color="#F56565" />
                                    <Text fontSize="xl" fontWeight="bold">
                                        Recent Discussions
                                    </Text>
                                </HStack>
                                <Button size="sm" colorScheme="purple" variant="outline" w={{ base: "full", md: "auto" }} onClick={onOpen}>
                                    New Topic
                                </Button>
                            </Flex>
                            <VStack spacing={4} align="stretch">
                                <Accordion allowToggle index={openDiscussionId !== null ? discussions.findIndex(d => d.id === openDiscussionId) : -1}>
                                    {discussions.map((d, idx) => (
                                        <MotionBox
                                            key={d.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.3, delay: idx * 0.07 }}
                                            whileHover={{ scale: 1.02 }}
                                        >
                                            <AccordionItem border="none">
                                                <h2>
                                                    <AccordionButton
                                                        _expanded={{ bg: "gray.700", color: "purple.300" }}
                                                        onClick={async () => {
                                                            if (openDiscussionId === d.id) {
                                                                setOpenDiscussionId(null);
                                                                setDiscussionComments([]);
                                                            } else {
                                                                setOpenDiscussionId(d.id);
                                                                const comments = await fetchDiscussionComments(d.id);
                                                                setDiscussionComments(comments);
                                                            }
                                                        }}
                                                    >
                                                        <Flex
                                                            direction={{ base: "column", md: "row" }}
                                                            justify="space-between"
                                                            align={{ base: "flex-start", md: "center" }}
                                                            gap={4}
                                                            flex="1"
                                                        >
                                                            <HStack align="start" spacing={4} flex={1} w={{ base: "full", md: "auto" }}>
                                                                <Avatar src={d.avatar} size="md" />
                                                                <VStack align="start" spacing={2} flex={1}>
                                                                    <HStack flexWrap="wrap">
                                                                        <Text
                                                                            fontSize="lg"
                                                                            fontWeight="semibold"
                                                                        >
                                                                            {d.title}
                                                                        </Text>
                                                                        {d.isHot && (
                                                                            <Badge
                                                                                colorScheme="red"
                                                                                variant="solid"
                                                                                display="flex"
                                                                                alignItems="center"
                                                                                gap={1}
                                                                            >
                                                                                <FaFire size={10} />
                                                                                HOT
                                                                            </Badge>
                                                                        )}
                                                                    </HStack>
                                                                    <HStack
                                                                        spacing={4}
                                                                        color="gray.400"
                                                                        fontSize="sm"
                                                                        flexWrap="wrap"
                                                                    >
                                                                        <Text>by {d.author}</Text>
                                                                        <Badge
                                                                            colorScheme="purple"
                                                                            variant="outline"
                                                                        >
                                                                            {d.category}
                                                                        </Badge>
                                                                    </HStack>
                                                                </VStack>
                                                            </HStack>
                                                            <VStack align={{ base: "flex-start", md: "end" }} spacing={2} minW={{ base: "full", md: "110px" }} mt={{ base: 4, md: 0 }}>
                                                                <HStack
                                                                    spacing={4}
                                                                    color="gray.400"
                                                                    fontSize="sm"
                                                                    flexWrap="wrap"
                                                                >
                                                                    <HStack>
                                                                        <FaComments />
                                                                        <Text>{d.replies}</Text>
                                                                    </HStack>
                                                                    <HStack>
                                                                        <FaUsers />
                                                                        <Text>{d.views}</Text>
                                                                    </HStack>
                                                                    <HStack>
                                                                        <IconButton
                                                                            icon={<FaThumbsUp />}
                                                                            size="xs"
                                                                            variant="ghost"
                                                                            colorScheme="green"
                                                                            aria-label="Upvote"
                                                                            onClick={(e) => handleVote(d.id, "up", e)}
                                                                        />
                                                                        <Text>{d.upvotes}</Text>
                                                                        <IconButton
                                                                            icon={<FaThumbsDown />}
                                                                            size="xs"
                                                                            variant="ghost"
                                                                            colorScheme="red"
                                                                            aria-label="Downvote"
                                                                            onClick={(e) => handleVote(d.id, "down", e)}
                                                                        />
                                                                    </HStack>
                                                                </HStack>
                                                                <Text fontSize="xs" color="gray.500">
                                                                    {d.lastActivity}
                                                                </Text>
                                                            </VStack>
                                                        </Flex>
                                                        <AccordionIcon />
                                                    </AccordionButton>
                                                </h2>
                                                <AccordionPanel pb={4} bg="gray.800" border="1px" borderColor="gray.700" mt={-1}>
                                                    <VStack align="start" spacing={4} p={4}>
                                                        <Text>{d.content}</Text>
                                                        <Divider borderColor="gray.700" />
                                                        <Text fontSize="md" fontWeight="bold">
                                                            Comments ({discussionComments.length})
                                                        </Text>
                                                        {discussionComments.map((comment, idx) => (
                                                            <HStack key={idx} align="start" spacing={3} w="full">
                                                                <Avatar src={comment.avatar} size="sm" />
                                                                <VStack align="start" spacing={0} flex={1}>
                                                                    <HStack>
                                                                        <Text fontWeight="semibold">{comment.author}</Text>
                                                                        <Text fontSize="xs" color="gray.500">
                                                                            {comment.timestamp}
                                                                        </Text>
                                                                    </HStack>
                                                                    <Text fontSize="sm">{comment.content}</Text>
                                                                </VStack>
                                                            </HStack>
                                                        ))}
                                                        <FormControl mt={4}>
                                                            <Flex>
                                                                <Input
                                                                    placeholder="Add a comment..."
                                                                    value={newCommentContent}
                                                                    onChange={(e) => setNewCommentContent(e.target.value)}
                                                                    bg="gray.700"
                                                                    border="none"
                                                                    _placeholder={{ color: "gray.400" }}
                                                                />
                                                                <IconButton
                                                                    icon={<FaPaperPlane />}
                                                                    ml={2}
                                                                    colorScheme="purple"
                                                                    aria-label="Add comment"
                                                                    onClick={async () => {
                                                                        if (newCommentContent.trim()) {
                                                                            const result = await addCommentToDiscussion(d.id, { content: newCommentContent });
                                                                            if (result.success) {
                                                                                setDiscussionComments((prev) => [...prev, result.newComment]);
                                                                                setNewCommentContent("");
                                                                            } else {
                                                                                alert(result.message);
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            </Flex>
                                                        </FormControl>
                                                    </VStack>
                                                </AccordionPanel>
                                            </AccordionItem>
                                        </MotionBox>
                                    ))}
                                </Accordion>
                            </VStack>
                        </MotionBox>
                    </GridItem>

                    <GridItem>
                        <VStack spacing={6} align="stretch">
                            <MotionBox
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <Card bg="gray.800" border="1px" borderColor="gray.700">
                                    <CardBody>
                                        <VStack align="start" spacing={4}>
                                            <Text
                                                fontSize="lg"
                                                fontWeight="bold"
                                                color="purple.400"
                                            >
                                                Community Stats
                                            </Text>
                                            <Divider borderColor="gray.600" />
                                            <VStack align="start" spacing={3} w="full">
                                                <HStack justify="space-between" w="full">
                                                    <HStack>
                                                        <FaUsers color="#68D391" />
                                                        <Text>Active Users</Text>
                                                    </HStack>
                                                    <Text
                                                        fontWeight="bold"
                                                        color="green.400"
                                                    >
                                                        {communityStats.activeUsers.toLocaleString()}
                                                    </Text>
                                                </HStack>
                                                <HStack justify="space-between" w="full">
                                                    <HStack>
                                                        <FaComments color="#9F7AEA" />
                                                        <Text>Total Posts</Text>
                                                    </HStack>
                                                    <Text
                                                        fontWeight="bold"
                                                        color="purple.400"
                                                    >
                                                        {communityStats.totalPosts.toLocaleString()}
                                                    </Text>
                                                </HStack>
                                                <HStack justify="space-between" w="full">
                                                    <HStack>
                                                        <FaStar color="#F6E05E" />
                                                        <Text>Expert Members</Text>
                                                    </HStack>
                                                    <Text
                                                        fontWeight="bold"
                                                        color="yellow.400"
                                                    >
                                                        156
                                                    </Text>
                                                </HStack>
                                            </VStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </MotionBox>

                            <MotionBox
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                <Card bg="gray.800" border="1px" borderColor="gray.700">
                                    <CardBody>
                                        <VStack align="start" spacing={4}>
                                            <Text
                                                fontSize="lg"
                                                fontWeight="bold"
                                                color="purple.400"
                                            >
                                                Leaderboard
                                            </Text>
                                            <Divider borderColor="gray.600" />
                                            <VStack align="start" spacing={3} w="full">
                                                {leaderboard.map((user, i) => (
                                                    <HStack key={user.name} justify="space-between" w="full">
                                                        <HStack>
                                                            <Avatar size="sm" name={user.name} src={user.avatar} />
                                                            <Text>{user.name}</Text>
                                                        </HStack>
                                                        <HStack>
                                                            <FaTrophy color={i === 0 ? "#FFD700" : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "#68D391"} />
                                                            <Text
                                                                color={i === 0 ? "yellow.400" : i === 1 ? "gray.400" : i === 2 ? "orange.400" : "green.400"}
                                                                fontWeight="bold"
                                                            >
                                                                {user.score}
                                                            </Text>
                                                        </HStack>
                                                    </HStack>
                                                ))}
                                            </VStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </MotionBox>

                            <MotionBox
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                            >
                                <Card bg="gray.800" border="1px" borderColor="gray.700">
                                    <CardBody>
                                        <VStack spacing={4}>
                                            <Text
                                                fontSize="lg"
                                                fontWeight="bold"
                                                color="purple.400"
                                            >
                                                Quick Actions
                                            </Text>
                                            <VStack spacing={3} w="full">
                                                <Button
                                                    colorScheme="purple"
                                                    size="sm"
                                                    w="full"
                                                    onClick={onOpen}
                                                >
                                                    Create Topic
                                                </Button>
                                                <Button
                                                    colorScheme="green"
                                                    size="sm"
                                                    w="full"
                                                    variant="outline"
                                                >
                                                    Browse Casino
                                                </Button>
                                                <Button
                                                    colorScheme="blue"
                                                    size="sm"
                                                    w="full"
                                                    variant="outline"
                                                >
                                                    Sports Betting
                                                </Button>
                                            </VStack>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            </MotionBox>
                        </VStack>
                    </GridItem>
                </Grid>
            </Container>

            {/* New Topic Modal (existing) */}
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent bg="gray.800" color="white">
                    <ModalHeader>Create New Topic</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <FormControl>
                            <FormLabel>Title</FormLabel>
                            <Input
                                name="title"
                                placeholder="Topic title"
                                value={newTopic.title}
                                onChange={handleNewTopicChange}
                                bg="gray.700"
                                border="none"
                            />
                        </FormControl>

                        <FormControl mt={4}>
                            <FormLabel>Category</FormLabel>
                            <Select
                                name="category"
                                value={newTopic.category}
                                onChange={handleNewTopicChange}
                                bg="gray.700"
                                border="none"
                            >
                                <option value="General">General</option>
                                <option value="Casino">Casino</option>
                                <option value="Sports">Sports</option>
                                <option value="Blog">Blog</option>
                                <option value="VIP">VIP</option>
                            </Select>
                        </FormControl>

                        <FormControl mt={4}>
                            <FormLabel>Content</FormLabel>
                            <Textarea
                                name="content"
                                placeholder="What's on your mind?"
                                value={newTopic.content}
                                onChange={handleNewTopicChange}
                                bg="gray.700"
                                border="none"
                            />
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        <Button colorScheme="purple" mr={3} onClick={handleCreateTopic}>
                            Create
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Forum